import { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../lib/api';

/**
 * Custom hook to fetch portfolio data with multiple benchmark comparisons
 * @param {string|string[]} category - Category name or array of categories for portfolio data
 * @param {number} days - Number of days to fetch (default: 90)
 * @returns {Object} { portfolioData, benchmarks, comparison, loading, error }
 */
export const useBenchmarkComparison = (category, days = 90) => {
  const [portfolioData, setPortfolioData] = useState([]);
  const [benchmarks, setBenchmarks] = useState({});
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Convert category to stable string for dependency tracking
  const categoryKey = Array.isArray(category) ? category.join(',') : category;

  useEffect(() => {
    const fetchBenchmarkComparison = async () => {
      try {
        setLoading(true);
        setError(null);

        // Calculate start date
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - days);

        // Fetch snapshots
        const response = await axios.get(`${API_BASE_URL}/snapshots`, {
          params: {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0]
          },
          withCredentials: true
        });

        if (response.data.success && response.data.snapshots) {
          const categories = Array.isArray(category) ? category : [category];

          // Extract portfolio data (sum of specified categories)
          const portfolio = response.data.snapshots
            .map(snapshot => {
              const categoryValue = categories.reduce((sum, cat) => {
                return sum + (snapshot.categories?.[cat]?.value || 0);
              }, 0);

              return {
                date: snapshot.date,
                value: categoryValue,
                benchmarks: snapshot.benchmarks || {}
              };
            })
            .filter(point => point.value > 0) // Filter out zero-value points
            .sort((a, b) => a.date.localeCompare(b.date));

          if (portfolio.length === 0) {
            setPortfolioData([]);
            setBenchmarks({});
            setComparison(null);
            return;
          }

          const firstPortfolioValue = portfolio[0].value;

          // Extract and normalize all benchmarks
          const benchmarkKeys = ['sp500', 'nasdaq', 'russell2000', 'dax', 'stoxx600', 'msciWorld', 'bitcoin'];
          const normalizedBenchmarks = {};

          benchmarkKeys.forEach(key => {
            const rawData = portfolio
              .map(point => ({
                date: point.date,
                value: point.benchmarks[key] || null
              }))
              .filter(point => point.value !== null);

            if (rawData.length > 0) {
              const firstBenchmarkValue = rawData[0].value;
              if (firstBenchmarkValue > 0) {
                const scaleFactor = firstPortfolioValue / firstBenchmarkValue;
                normalizedBenchmarks[key] = rawData.map(point => ({
                  date: point.date,
                  value: point.value * scaleFactor,
                  originalValue: point.value
                }));
              }
            } else {
              normalizedBenchmarks[key] = [];
            }
          });

          // Calculate performance comparison for S&P 500 (default benchmark)
          let performanceComparison = null;
          if (portfolio.length > 0 && normalizedBenchmarks.sp500?.length > 0) {
            const portfolioStart = portfolio[0].value;
            const portfolioEnd = portfolio[portfolio.length - 1].value;
            const sp500Start = normalizedBenchmarks.sp500[0].value;
            const sp500End = normalizedBenchmarks.sp500[normalizedBenchmarks.sp500.length - 1].value;

            const portfolioReturn = portfolioStart > 0
              ? ((portfolioEnd - portfolioStart) / portfolioStart) * 100
              : 0;

            const sp500Return = sp500Start > 0
              ? ((sp500End - sp500Start) / sp500Start) * 100
              : 0;

            performanceComparison = {
              portfolioReturn,
              sp500Return,
              outperformance: portfolioReturn - sp500Return,
              portfolioChange: portfolioEnd - portfolioStart,
              sp500Change: sp500End - sp500Start
            };
          }

          setPortfolioData(portfolio);
          setBenchmarks(normalizedBenchmarks);
          setComparison(performanceComparison);
        } else {
          setPortfolioData([]);
          setBenchmarks({});
          setComparison(null);
        }
      } catch (err) {
        console.error(`Error fetching benchmark comparison for ${categoryKey}:`, err);
        setError(err.response?.data?.error || err.message || 'Failed to fetch benchmark data');
        setPortfolioData([]);
        setBenchmarks({});
        setComparison(null);
      } finally {
        setLoading(false);
      }
    };

    fetchBenchmarkComparison();
    // `category` is intentionally omitted: callers (e.g. Portfolio.js) pass
    // new array literals on every render, so depending on `category`
    // directly would refetch on every render (infinite loop from the
    // effect's own setState calls re-rendering the caller). `categoryKey`
    // (derived above via .join(',')) is the deliberately-stabilized proxy
    // for `category`'s value; the closure still reads the latest `category`
    // at call time, which is always in sync with `categoryKey` since both
    // are recomputed from the same render's prop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryKey, days]);

  return { portfolioData, benchmarks, comparison, loading, error };
};
